import { v4 } from 'uuid';
import { ConsolidatedLocationView } from '@mark43/cad-api';
import { EventInfo } from '../../../common/broadcast-channel/types';
import {
    getLocationSummaryFirstLineText,
    getLocationSummarySecondLineText,
    getLocationSummaryThirdLineText,
} from '../../../location-select/helpers/location-summary';
import { CALL_PHONE_TYPE_MAP, DEFAULT_COS_KEY, INCIDENT_ID_MAX_LENGTH } from '../constants';
import { formatPhoneNumber } from './person-helpers';
import { NormalizedPhoneCallDisplay } from '~/phone-calls/types';
import { logWarn } from '../../../monitoring/log';

export type Discipline = 'EMS' | 'FIRE' | 'POLICE';
const TEMP_INCIDENT_ID_REGEX = new RegExp(`^[a-f0-9]{${INCIDENT_ID_MAX_LENGTH}}$`, 'i');

interface BuildEventInfoParams {
    metadata: {
        eventId?: number;
        eventCreatedDateUtc?: string;
        callTaker?: string;
        agencyEventNumber?: string;
    };
    location: ConsolidatedLocationView | null;
    reportingParty: {
        firstName?: string | null;
        lastName?: string | null;
        phoneNumber?: string | null;
    };
    phoneCallDisplay?: NormalizedPhoneCallDisplay;
    enhancementsEnabled?: boolean;
    initiatedDateTimeUtc?: string | null;
}

export function generateTempIncidentId(): string {
    return v4().replace(/-/g, '').slice(0, INCIDENT_ID_MAX_LENGTH);
}

export function normalizeDiscipline(value?: string | null): Discipline | undefined {
    if (!value) {
        return undefined;
    }
    const trimmed = value.trim().toUpperCase();
    switch (trimmed) {
        case 'P':
            return 'POLICE';
        case 'F':
            return 'FIRE';
        case 'M':
            return 'EMS';
        default:
            return undefined;
    }
}

export function isTemporaryIncidentId(value?: string | null): value is string {
    if (!value) {
        return false;
    }
    return TEMP_INCIDENT_ID_REGEX.test(value.trim());
}

export function buildOverwriteSignature(
    previousIncidentNumber: string,
    attemptedIncidentNumber: string
): string {
    return `${previousIncidentNumber.trim()}->${attemptedIncidentNumber.trim()}`;
}

export type RetryOverwriteAction = {
    type: 'OVERWRITE_PRO_QA_INCIDENT_NUMBER';
    payload: {
        type: Discipline;
        incidentNumber: string;
        overIncidentNumber: string;
    };
};

/**
 * Decides whether an Open ProQA click should re-fire OVERWRITE_PRO_QA_INCIDENT_NUMBER.
 * Returns the action payload when a previous NEWCASENO was sent for the same
 * (temp -> saved) pair but no SUCCEEDED or FAILED has arrived (ProQA silence).
 * Returns null otherwise — already migrated, explicitly rejected, or no migration
 * was ever in flight.
 */
export function decideUnackedNewCaseNoRetry({
    discipline,
    tempIncidentId,
    savedEventNumber,
    pendingSignature,
}: {
    discipline: Discipline;
    tempIncidentId: string | undefined;
    savedEventNumber: string | undefined;
    pendingSignature: string | undefined;
}): RetryOverwriteAction | null {
    if (!tempIncidentId || !savedEventNumber || !isTemporaryIncidentId(tempIncidentId)) {
        return null;
    }
    const signature = buildOverwriteSignature(tempIncidentId, savedEventNumber);
    if (pendingSignature !== signature) {
        return null;
    }
    return {
        type: 'OVERWRITE_PRO_QA_INCIDENT_NUMBER',
        payload: {
            type: discipline,
            incidentNumber: tempIncidentId,
            overIncidentNumber: savedEventNumber,
        },
    };
}

export function logMissingParameterWarning(parameterName: string) {
    logWarn(`Missing ${parameterName} for ProQA launch`, {
        parameterName,
    });
}

/*
 * Determines if event is saved based on eventId.
 * Logs invalid agencyEventNumber for draft events.
 */
export function checkIsSavedEvent(eventId?: number, agencyEventNumber?: string): boolean {
    const isSaved = eventId !== undefined;

    if (!isSaved && agencyEventNumber !== undefined) {
        const trimmed = agencyEventNumber.trim();

        if (trimmed === '' || trimmed === '0') {
            logWarn(`Invalid agencyEventNumber detected on draft event: "${agencyEventNumber}"`, {
                agencyEventNumber,
            });
        }
    }

    return isSaved;
}

export function buildEventInfo({
    metadata,
    location,
    reportingParty,
    phoneCallDisplay,
    enhancementsEnabled,
    initiatedDateTimeUtc,
}: BuildEventInfoParams): EventInfo {
    const formattedReportingPartyName = [reportingParty.firstName, reportingParty.lastName]
        .filter(Boolean)
        .join(' ');

    const formattedReportingPartyPhoneNumber = reportingParty.phoneNumber
        ? formatPhoneNumber(reportingParty.phoneNumber)
        : '';

    return {
        id: metadata.eventId ?? 0,
        createdDateUtc:
            initiatedDateTimeUtc ?? metadata.eventCreatedDateUtc ?? new Date().toISOString(),

        formattedCallTaker: metadata.callTaker,
        formattedDispatcher: 'Could not find Call Taker name',
        eventLocationThreeLineDisplay: {
            line1: getLocationSummaryFirstLineText(location),
            line2: getLocationSummarySecondLineText(location),
            line3: getLocationSummaryThirdLineText(location),
        },
        eventLocation: {
            adminArea1: location?.adminArea1,
            latitude: location?.latitude?.toString() ?? '',
            longitude: location?.longitude?.toString() ?? '',
            locality: location?.locality,
            postalCode: location?.postalCode,
        },
        reportingParties: [
            {
                formattedReportingPerson: formattedReportingPartyName,
                phoneNumber: { phoneNumber: formattedReportingPartyPhoneNumber },
            },
        ],
        callPhoneType: phoneCallDisplay
            ? CALL_PHONE_TYPE_MAP[
                  phoneCallDisplay.classOfService?.toUpperCase() ?? DEFAULT_COS_KEY
              ] ?? CALL_PHONE_TYPE_MAP[DEFAULT_COS_KEY]
            : reportingParty.phoneNumber?.trim()
            ? CALL_PHONE_TYPE_MAP[DEFAULT_COS_KEY]
            : undefined,
        agencyEventNumber: metadata.agencyEventNumber,
        incidentNumber: enhancementsEnabled ? metadata.agencyEventNumber : metadata.eventId ?? 0,
        secondarySession: undefined,
    };
}
